import { useEffect, useState } from "react";
import type { TranslatedString } from "@i18n";
import { useTranslation } from "@i18n";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";

import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import Text from "@CubeComponents/html/Text/Text";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { refreshFileManager } from "@Actions/fileUpload";

import RecursiveConfirmationPopup from "../RecursiveConfirmationPopup";
import {
  fetchRevisionSeries,
  fetchRevisionSeriesList,
  setRevisionSeries,
  type RevisionSeriesOption,
} from "../revisionSeriesShared";

type Props = { folderId: string };

function FolderRevisionSeriesContent({ folderId }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [seriesList, setSeriesList] = useState<RevisionSeriesOption[]>([]);
  const [currentSeriesId, setCurrentSeriesId] = useState<string | null>(null);
  const [pendingSeriesId, setPendingSeriesId] = useState<string | null>(null);
  const [isCascadeOverride, setIsCascadeOverride] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [list, current] = await Promise.all([fetchRevisionSeriesList(), fetchRevisionSeries(folderId)]);
      if (!cancelled) {
        setSeriesList(list);
        setCurrentSeriesId(current.revisionSeriesId);
        setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [folderId]);

  const showToast = (message: string, type: typeof TOAST_TYPE_SUCCESS | typeof TOAST_TYPE_ERROR) =>
    dispatch(showToastNotification({ content: message as TranslatedString, type }));

  const applyChange = async (revisionSeriesId: string, cascadeOverride: boolean) => {
    const response = await setRevisionSeries({ resourceId: folderId, revisionSeriesId, cascadeOverride });
    if (response.status) {
      setCurrentSeriesId(revisionSeriesId);
      dispatch(refreshFileManager(true));
      showToast(t("Revision Series updated"), TOAST_TYPE_SUCCESS);
    } else {
      showToast(response.errorMessage || t("The Revision Series could not be updated"), TOAST_TYPE_ERROR);
    }
  };

  const handleSelectionChange = (newValue: RevisionSeriesOption | null) => {
    if (!newValue || newValue._id === currentSeriesId) return;

    if (currentSeriesId == null) {
      applyChange(newValue._id, false);
      return;
    }

    setPendingSeriesId(newValue._id);
    setIsCascadeOverride(false);
  };

  const closeConfirmation = () => {
    setPendingSeriesId(null);
    setIsCascadeOverride(false);
  };

  const confirmChange = async () => {
    if (!pendingSeriesId) return;
    await applyChange(pendingSeriesId, isCascadeOverride);
    closeConfirmation();
  };

  const selectedSeries = seriesList.find((s) => s._id === currentSeriesId) ?? null;

  return (
    <div className="flex flex-col gap-4 p-3">
      <div className="flex items-center justify-between gap-3">
        <Text className="text-sm font-medium">{t("Revision Series")}</Text>
        <div className="min-w-[14rem]">
          <AutoComplete
            options={seriesList}
            value={selectedSeries}
            disabled={isLoading}
            getOptionLabel={(option: RevisionSeriesOption) => option?.name || ""}
            isOptionEqualToValue={(option: RevisionSeriesOption, value: RevisionSeriesOption) =>
              option?._id === value?._id
            }
            onChange={(_event: unknown, newValue: RevisionSeriesOption | null) => handleSelectionChange(newValue)}
            renderInput={(params) => <TextFieldComponent {...params} placeholder={t("Select Revision Series")} />}
          />
        </div>
      </div>

      {pendingSeriesId && (
        <RecursiveConfirmationPopup
          headerTitle={t("Change Revision Series")}
          additionalTextBeforeOptions={t("Are you sure you want to change Revision Series?")}
          radioButtonLabels={[
            t("Override the Revision Series in all existing Files and Folders"),
            t(
              "Retain Revision Series in existing Files and Folders and new Files and Folders will contain the updated Revision Series",
            ),
          ]}
          recursiveOverrideWarningMessage={t(
            "Overriding the Revision Series will reset the current Revision nomenclature. All Revisions will be renamed according to the new series.",
          )}
          optionButtonsText={[t("Confirm"), t("Cancel")]}
          isRecursive={isCascadeOverride}
          toggleIsRecursive={(event: React.ChangeEvent<HTMLInputElement>) =>
            setIsCascadeOverride(event.target.value === "overRide")
          }
          handleSubmit={confirmChange}
          handleClose={closeConfirmation}
        />
      )}
    </div>
  );
}

export default FolderRevisionSeriesContent;
